import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Check } from 'lucide-react';
import { DOCUMENT_TEMPLATES } from '../lib/documentTemplates';

const TemplateSelector = ({ selectedTemplate, onSelect }) => {
  const templates = Object.values(DOCUMENT_TEMPLATES);

  // Template preview colors for visual representation
  const templatePreviews = {
    classic: {
      header: '#ffffff',
      accent: '#0B6B4F',
      body: '#F7F6F2',
    },
    modern: {
      header: '#0C1F1A',
      accent: '#0B6B4F',
      body: '#F7F6F2',
    },
    corporate: {
      header: '#1e293b',
      accent: '#3b82f6',
      body: '#f1f5f9',
    },
    compact: {
      header: '#f3f4f6',
      accent: '#6366f1',
      body: '#ffffff',
    },
    elegant: {
      header: 'linear-gradient(135deg, #0C1F1A 0%, #0B6B4F 100%)',
      accent: '#d4af37',
      body: '#faf5e6',
    },
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {templates.map((template) => {
        const isSelected = selectedTemplate === template.id;
        const preview = templatePreviews[template.id];
        
        return (
          <Card
            key={template.id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              isSelected 
                ? 'ring-2 ring-teal-500 shadow-md' 
                : 'hover:ring-1 hover:ring-slate-300'
            }`}
            onClick={() => onSelect(template.id)}
            data-testid={`template-${template.id}`}
          >
            <CardContent className="p-3">
              {/* Template Preview */}
              <div className="relative aspect-[3/4] rounded-md overflow-hidden border border-[#E5E2D9] mb-2">
                {/* Header */}
                <div 
                  className="h-1/4 flex items-center justify-between px-2"
                  style={{ background: preview.header }}
                >
                  <div className="flex flex-col gap-0.5">
                    <div 
                      className="w-8 h-1 rounded"
                      style={{ background: template.id === 'classic' ? preview.accent : 'rgba(255,255,255,0.8)' }}
                    />
                    <div 
                      className="w-5 h-0.5 rounded"
                      style={{ background: template.id === 'classic' ? '#94a3b8' : 'rgba(255,255,255,0.5)' }}
                    />
                  </div>
                  <div 
                    className="w-6 h-2 rounded text-[4px] font-bold flex items-center justify-center"
                    style={{ 
                      background: template.id === 'classic' ? preview.accent : 'rgba(255,255,255,0.2)',
                      color: template.id === 'classic' ? '#fff' : 'rgba(255,255,255,0.9)'
                    }}
                  >
                    INV
                  </div>
                </div>
                
                {/* Body */}
                <div 
                  className="h-2/4 p-2 flex flex-col gap-1"
                  style={{ background: '#ffffff' }}
                >
                  {/* Table header */}
                  <div 
                    className="h-2 rounded-sm"
                    style={{ background: preview.body }}
                  />
                  {/* Table rows */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-1">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-sm" />
                      <div className="w-4 h-1.5 bg-slate-100 rounded-sm" />
                    </div>
                  ))}
                  {/* Total */}
                  <div className="flex justify-end mt-auto">
                    <div 
                      className="w-8 h-2 rounded-sm"
                      style={{ background: preview.accent }}
                    />
                  </div>
                </div>
                
                {/* Footer */}
                <div 
                  className="h-1/4 px-2 py-1"
                  style={{ background: preview.body }}
                >
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <div className="w-full h-0.5 bg-slate-200 rounded mb-0.5" />
                      <div className="w-2/3 h-0.5 bg-slate-200 rounded" />
                    </div>
                    <div className="flex-1">
                      <div className="w-full h-0.5 bg-slate-200 rounded mb-0.5" />
                      <div className="w-2/3 h-0.5 bg-slate-200 rounded" />
                    </div>
                  </div>
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>

              {/* Template Info */}
              <div className="text-center">
                <p className={`text-sm font-medium ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                  {template.name}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">
                  {template.description}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default TemplateSelector;
